import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X, ArrowRight, Heart, UserRound, ChevronDown } from 'lucide-react';
import { useCart } from '@/lib/cartContext';
import { useFavorites } from '@/lib/favoritesStore';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { Image } from '@/components/ui/image';
import { teamSlug, teamDisplay } from '@/lib/teamRoutes';
import StoreStatusBadge from '@/components/StoreStatusBadge';

const links = [
  { label: 'ÚRVALIÐ', to: '/safnid' },
  { label: 'TREYJA VIKUNNAR', hash: 'treyja-vikunnar' },
  { label: 'SPURNING DAGSINS', to: '/?open=treyjuthraut' },
  { label: 'ÓSKA EFTIR TREYJU', to: '/draumatreyja' },
  { label: 'SELJA MEÐ OKKUR', to: '/seldu' },
  { label: 'KÍKTU TIL OKKAR', to: '/heimsokn' },
  { label: 'SPURNINGAR', to: '/spurningar' },
  { label: 'UM OKKUR', to: '/um-okkur' },
];



function useCountdown(target) {
  const calc = () => {
    if (!target) return { days: 0, hours: 0, minutes: 0, seconds: 0, done: false };
    const diff = Math.max(0, new Date(target).getTime() - Date.now());
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return { days, hours, minutes, seconds, done: diff <= 0 };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    setTime(calc());
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, [target]);
  return time;
}

function DropBanner() {
  const [campaign, setCampaign] = useState(null);

  useEffect(() => {
    let active = true;
    const refresh = () => base44.functions.invoke('getPublicDrop', {})
      .then((res) => { if (active) setCampaign(res.data); })
      .catch(() => {});
    refresh();
    // Refresh the public banner periodically without exposing the early-access token.
    const id = setInterval(refresh, 60000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const dropTime = campaign?.drop_time;
  const { days, hours, minutes, seconds, done } = useCountdown(dropTime);

  if (!campaign?.enabled || !dropTime) return null;
  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div className="bg-secondary text-secondary-foreground">
      {done ? (
        <Link to="/safnid" className="w-full max-w-[1400px] mx-auto px-4 md:px-8 py-2 flex items-center justify-center gap-2 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.15em] hover:opacity-80">
          <span>{campaign.label || 'Nýtt drop er komið'}</span>
          <ArrowRight className="w-3.5 h-3.5" />
          <span>Versla nú</span>
        </Link>
      ) : (
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8 py-2.5 flex items-center justify-center gap-2 md:gap-4">
          <span className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.15em] text-secondary-foreground/80">
            {campaign.label || 'Nýtt drop byrjar eftir'}
          </span>
          <div className="flex items-center gap-1 md:gap-1.5">
            {[
              { val: days, label: 'DAGAR' },
              { val: hours, label: 'KLST' },
              { val: minutes, label: 'MÍN' },
              { val: seconds, label: 'SEK' },
            ].map((unit, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="text-secondary-foreground/50 font-mono text-xs md:text-sm">:</span>}
                <div className="flex flex-col items-center bg-background/15 px-2 py-1 md:px-2.5 md:py-1.5 rounded-sm">
                  <span className="font-mono font-bold tabular-nums text-cream text-base md:text-lg leading-none" style={{ color: 'hsl(40 30% 93%)' }}>
                    {pad(unit.val)}
                  </span>
                  <span className="font-mono text-[7px] md:text-[8px] uppercase tracking-[0.1em] text-secondary-foreground/60 mt-0.5">
                    {unit.label}
                  </span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Nav() {
  const { count, setIsOpen } = useCart();
  const { favorites } = useFavorites();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [mobileTeamsOpen, setMobileTeamsOpen] = useState(false);
  const [teams, setTeams] = useState([]);
  useEffect(() => {
    base44.entities.Product.filter({ published: true }, '-created_date', 500, 0, ['team', 'published']).then(rows => {
      const names = [...new Set((rows || []).filter(p => p.published && p.team).map(p => p.team))];
      setTeams(names.sort((a, b) => teamDisplay(a).localeCompare(teamDisplay(b), 'is')));
    }).catch(() => {});
  }, []);
  const navigate = useNavigate();
  const location = useLocation();

  const goSection = (hash) => {
    if (location.pathname === '/') {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' }), 300);
    }
  };

  const handleLink = (l) => {
    setOpen(false);
    if (l.to) navigate(l.to);
    else if (l.hash) goSection(l.hash);
  };

  return (
    <header className="sticky top-0 z-40 bg-background border-b border-border">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
        {/* LOGO */}
        <button onClick={() => navigate('/')} className="flex items-center gap-3 flex-shrink-0">
          <Image src="https://media.base44.com/images/public/6aa2c2af54cfb5bf4d045125/41cbee118_Untitleddesign10.png" alt="Vintage Treyjur" width="56" height="56" loading="eager" fittingType="fit" className="w-14 h-14 md:w-16 md:h-16 object-contain flex-shrink-0" />
          <div className="hidden sm:block leading-none">
            <p className="font-heading text-sm md:text-base text-primary">Vintage Treyjur</p>
            <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-muted-foreground mt-0.5">Reykjavík · Est. 2026</p>
          </div>
        </button>

        {/* CENTER NAV */}
        <nav className="hidden lg:flex items-center gap-5 font-mono text-[10px] uppercase tracking-[0.15em]">
          <StoreStatusBadge />
          <Link to="/safnid" className="text-foreground hover:text-accent transition-colors whitespace-nowrap">ALLAR TREYJUR</Link>
          <div className="relative group">
            <div className="flex items-center gap-1">
              <button onClick={() => navigate('/safnid')} className="text-foreground hover:text-accent transition-colors whitespace-nowrap">LIÐ</button>
              <button aria-label="Skoða lið" aria-haspopup="true" className="p-1 text-foreground hover:text-accent"><ChevronDown className="w-3 h-3" /></button>
            </div>
            <div className="absolute left-0 top-full w-56 max-h-80 overflow-y-auto hidden group-hover:flex group-focus-within:flex flex-col bg-background border border-border shadow-lg py-2 z-50 normal-case">
              <Link to="/safnid" className="px-4 py-2 hover:bg-muted">Allar treyjur</Link>
              {teams.map(team => <Link key={team} to={`/lid/${teamSlug(team)}`} className="px-4 py-2 hover:bg-muted">{teamDisplay(team)}</Link>)}
            </div>
          </div>
          <button onClick={() => handleLink({ to: '/seldu' })} className="text-foreground hover:text-accent transition-colors whitespace-nowrap">SELJA TREYJU</button>
          <button onClick={() => handleLink({ to: '/heimsokn' })} className="text-foreground hover:text-accent transition-colors whitespace-nowrap">HEIMSÓKN</button>
          <div className="relative group">
            <button className="text-foreground hover:text-accent inline-flex items-center gap-1 py-4" aria-label="Fleira" aria-haspopup="true">FLEIRA <ChevronDown className="w-3 h-3" /></button>
            <div className="absolute right-0 top-full w-56 hidden group-hover:flex group-focus-within:flex flex-col bg-background border border-border shadow-lg py-2 z-50">
              {links.filter(l => !['ÚRVALIÐ','SELJA MEÐ OKKUR','KÍKTU TIL OKKAR'].includes(l.label)).map(l => (
                <button key={l.label} onClick={() => handleLink(l)} className="text-left px-4 py-3 text-foreground hover:bg-muted hover:text-accent">{l.label}</button>
              ))}
            </div>
          </div>
        </nav>

        {/* RIGHT */}
        <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
          <button onClick={() => navigate(user ? '/reikningur' : '/login')} className="relative p-2 hover:text-accent transition-colors" aria-label={user ? 'Mitt svæði' : 'Innskráning'}>
            <UserRound className="w-5 h-5" />
          </button>
          <button onClick={() => navigate('/uppahald')} className="relative p-2 hover:text-accent transition-colors" aria-label="Uppáhald">
            <Heart className="w-5 h-5" />
            {favorites.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-accent text-accent-foreground text-[9px] font-mono font-bold w-4 h-4 rounded-full flex items-center justify-center">
                {favorites.length}
              </span>
            )}
          </button>
          <button onClick={() => setIsOpen(true)} className="relative p-2 hover:text-accent transition-colors" aria-label="Karfa">
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-accent text-accent-foreground text-[9px] font-mono font-bold w-4 h-4 rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} aria-label="Valmynd" aria-expanded={open}>
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* MOBILE MENU */}
      {open && (
        <div className="lg:hidden border-t border-border bg-background max-h-[calc(100dvh-4rem)] overflow-y-auto">
          <nav className="px-4 py-5 font-mono text-xs uppercase tracking-widest">
            <div className="flex flex-col gap-4 pb-5 border-b border-border">
              <div className="min-h-11 flex items-center">
                <StoreStatusBadge />
              </div>
              <Link to="/safnid" onClick={() => setOpen(false)} className="min-h-11 flex items-center text-foreground hover:text-accent">ALLAR TREYJUR</Link>
              <div className="flex items-center justify-between">
                <button onClick={() => handleLink(links[0])} className="text-left text-foreground hover:text-accent">LIÐ</button>
                <button aria-label="Skoða lið" aria-expanded={mobileTeamsOpen} onClick={() => setMobileTeamsOpen(v => !v)} className="p-2"><ChevronDown className={`w-4 h-4 transition-transform ${mobileTeamsOpen ? 'rotate-180' : ''}`} /></button>
              </div>
              {mobileTeamsOpen && <div className="pl-4 max-h-52 overflow-y-auto flex flex-col gap-3 text-[11px]">
                {teams.map(team => <Link key={team} to={`/lid/${teamSlug(team)}`} onClick={() => setOpen(false)} className="text-left text-foreground hover:text-accent transition-colors">{teamDisplay(team)}</Link>)}
              </div>}
              <button onClick={() => { navigate('/uppahald'); setOpen(false); }} className="text-left text-foreground hover:text-accent">UPPÁHALD</button>
              <button onClick={() => { navigate(user ? '/reikningur' : '/login'); setOpen(false); }} className="text-left text-foreground hover:text-accent">{user ? 'MITT SVÆÐI' : 'INNSKRÁNING'}</button>
              <button onClick={() => handleLink({ to: '/draumatreyja' })} className="text-left text-foreground hover:text-accent">ÓSKA EFTIR TREYJU</button>
              <button onClick={() => handleLink({ to: '/seldu' })} className="text-left text-foreground hover:text-accent">SELJA MEÐ OKKUR</button>
            </div>
            <div className="flex flex-col gap-3 pt-5 text-muted-foreground text-[11px]">
              <button onClick={() => handleLink({ hash: 'treyja-vikunnar' })} className="text-left hover:text-accent">TREYJA VIKUNNAR</button>
              <button onClick={() => handleLink({ to: '/?open=treyjuthraut' })} className="text-left hover:text-accent">SPURNING DAGSINS</button>
              <button onClick={() => handleLink({ to: '/heimsokn' })} className="text-left hover:text-accent">KÍKTU TIL OKKAR</button>
              <button onClick={() => handleLink({ to: '/spurningar' })} className="text-left hover:text-accent">SPURNINGAR</button>
              <button onClick={() => handleLink({ to: '/um-okkur' })} className="text-left hover:text-accent">UM OKKUR</button>
              <button onClick={() => handleLink({ to: '/skilmalar' })} className="text-left hover:text-accent">SKILMÁLAR & AFHENDING</button>
            </div>
          </nav>
        </div>
      )}

      {/* DROP BANNER */}
      <div className="relative"><DropBanner /></div>
    </header>
  );
}